import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { findOrders } from '../../api/action/order';
import { findInvoicesByCriteria } from '../../api/action/invoice';
import { findBulkOrders } from '../../api/action/bulkOrder';
import Pagination from '../../components/Pagination';
import Loading from '../LandingPage/InstitutionDomain/Enquiry/components/Loading';
import OrderLayout from '../Profile/OrderSection/OrderLayout';
import { getRefundAwareOrderStatus } from '../../utils/cancellationRefundStatus';

const isBulkEnabledOrder = (order) => {
    if (!order) return false;
    if (Boolean(order.bulkOrderEnabled)) return true;

    return (order.lineItemList || []).some((lineItem) => (
        Boolean(lineItem?.product?.bulkOrderEnabled)
        || Boolean(lineItem?.scheduledProduct?.bulkOrderEnabled)
        || Boolean(lineItem?.scheduledProduct?.product?.bulkOrderEnabled)
    ));
};

const ViewOrders = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [orderDetailsArray, setOrderDetailsArray] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isServerError, setIsServerError] = useState(false);
    const [accountId, setAccountId] = useState(null);
    const [sortByPriceAsc, setSortByPriceAsc] = useState(true);
    const [sortByRecentAsc, setSortByRecentAsc] = useState(true);
    const [activeTab, setActiveTab] = useState("orders");
    const [bulkGroups, setBulkGroups] = useState([]);
    const [payingOrderId, setPayingOrderId] = useState(null);
    const [bulkPaymentError, setBulkPaymentError] = useState('');
    const domainType = localStorage.getItem("domainType");
    const userDetail = useSelector((state) => state?.auth?.data);
    const companyState = useSelector((state) => state?.companyReducer?.company);
    const persistedRoot = JSON.parse(localStorage.getItem("persist:root") || "{}");
    const persistedCompanyReducer = JSON.parse(persistedRoot?.companyReducer || "{}");
    const companyPrimaryId =
        companyState?.primaryId ||
        persistedCompanyReducer?.company?.primaryId ||
        localStorage.getItem("companyPrimaryId");

    // Fetch account ID from localStorage
    useEffect(() => {
        // const userData = JSON.parse(localStorage.getItem("user"));
        const userData = userDetail;
        const accountId = userData?.account?.primaryId;
        setAccountId(accountId);
    }, [userDetail]);

    // Fetch orders from API
    useEffect(() => {
        const fetchOrders = async () => {
            try {
                if (accountId) {
                    const fetchedOrders = await dispatch(findOrders(
                        accountId,
                        companyPrimaryId,
                        domainType === "RETAIL" ? null : "PAID"
                    ));
                    if (fetchedOrders.length > 0) {
                        fetchedOrders.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
                        setOrderDetailsArray(fetchedOrders);
                    } else {
                        setOrderDetailsArray([]);
                    }
                } else {
                    setOrderDetailsArray([]);
                }
            } catch (error) {
                if (error.message === "Network Error") {
                    setIsServerError(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrders();
    }, [dispatch, accountId, companyPrimaryId, domainType]);

    // The group response is the persisted source for aggregate quantity and
    // amount progress. The order status remains the source of truth for Pay Now.
    useEffect(() => {
        let active = true;
        const loadBulkGroups = async () => {
            if (!companyPrimaryId || domainType !== 'RETAIL') {
                setBulkGroups([]);
                return;
            }
            try {
                const response = await dispatch(findBulkOrders({ company: { primaryId: companyPrimaryId } }));
                const groups = Array.isArray(response)
                    ? response
                    : response?.data || response?.payload || response?.items || response?.results || [];
                if (active) setBulkGroups(Array.isArray(groups) ? groups : []);
            } catch (error) {
                if (active) setBulkGroups([]);
            }
        };
        loadBulkGroups();
        return () => { active = false; };
    }, [dispatch, companyPrimaryId, domainType]);

    const bulkGroupByOrderId = useMemo(() => {
        const lookup = new Map();
        bulkGroups.forEach((group) => {
            (group?.orderList || []).forEach((order) => {
                if (order?.primaryId != null) lookup.set(String(order.primaryId), group);
            });
        });
        return lookup;
    }, [bulkGroups]);

    const getBulkProgress = (order) => {
        const group = bulkGroupByOrderId.get(String(order?.primaryId));
        if (!group) return null;
        const activeOrders = (group.orderList || []).filter((item) => getRefundAwareOrderStatus(item) !== 'CANCELLED');
        const bookedQuantity = activeOrders.reduce((total, item) => total + Number(item?.lineItemList?.[0]?.quantity || 0), 0);
        const bookedAmount = activeOrders.reduce((total, item) => total + Number(item?.totalAmount || item?.totalAmtAfterDiscount || item?.dueAmount || 0), 0);
        const minimumQuantity = Number(companyState?.configuration?.minimumBulkOrderQuantity || 0);
        const minimumAmount = Number(companyState?.configuration?.minimumBulkOrderAmount || 0);
        return { group, bookedQuantity, bookedAmount, minimumQuantity, minimumAmount };
    };

    const handleBulkPayNow = async (order) => {
        if (!isBulkEnabledOrder(order) || order.currentStatus !== 'PAYMENT_PENDING' || !order.primaryId) return;
        setPayingOrderId(order.primaryId);
        setBulkPaymentError('');
        try {
            const response = await dispatch(findInvoicesByCriteria({ order: { primaryId: order.primaryId } }));
            const invoices = Array.isArray(response)
                ? response
                : response?.data || response?.payload || response?.items || response?.results || [];
            const invoice = (Array.isArray(invoices) ? invoices : []).find((item) =>
                item?.status !== 'CANCELLED' && item?.status !== 'PAID'
            );
            if (!invoice?.primaryId) throw new Error('The payment invoice is not available yet. Please refresh and try again.');
            const paymentUser = invoice.user || userDetail;
            const phoneNumber = paymentUser?.phoneNumber || paymentUser?.phone;
            if (!phoneNumber) throw new Error('A phone number is required to start payment.');
            navigate('/invoice-payment', {
                state: {
                    phoneNumber,
                    invoiceId: invoice.primaryId,
                    amount: invoice?.order?.totalAmount ?? invoice?.amount ?? order?.totalAmount ?? 0,
                    order: invoice.order || order,
                    user: paymentUser,
                    invoice,
                    originalPaymentMethod: invoice.paymentMethod || order.paymentMethod
                }
            });
        } catch (error) {
            setBulkPaymentError(error?.message || 'Unable to start payment for this bulk order.');
        } finally {
            setPayingOrderId(null);
        }
    };

    // Memoized filtering for cancelled and active orders
    const cancelledOrders = useMemo(() => (
        orderDetailsArray.filter(order => getRefundAwareOrderStatus(order) === "CANCELLED")
    ), [orderDetailsArray]);

    const allOrders = useMemo(() => (
        orderDetailsArray.filter(order => getRefundAwareOrderStatus(order) !== "CANCELLED")
    ), [orderDetailsArray]);

    // Sorting logic for price and recent
    const toggleSortByPrice = () => {
        const sortedOrders = [...orderDetailsArray];
        sortedOrders.sort((a, b) => sortByPriceAsc ? a.totalAmount - b.totalAmount : b.totalAmount - a.totalAmount);
        setOrderDetailsArray(sortedOrders);
        setSortByPriceAsc(prev => !prev);
    };

    const toggleSortByRecent = () => {
        const sortedOrders = [...orderDetailsArray];
        sortedOrders.sort((a, b) => sortByRecentAsc ? new Date(b.createdDate) - new Date(a.createdDate) : new Date(a.createdDate) - new Date(b.createdDate));
        setOrderDetailsArray(sortedOrders);
        setSortByRecentAsc(prev => !prev);
    };

    // Pagination logic
    const [currentPage, setCurrentPage] = useState(1);
    const ordersPerPage = 10;
    const lastIndex = currentPage * ordersPerPage;
    const firstIndex = lastIndex - ordersPerPage;
    const ordersToDisplay = activeTab === "orders" ? allOrders : cancelledOrders;
    const currentOrders = ordersToDisplay.slice(firstIndex, lastIndex);
    const totalPages = Math.ceil(ordersToDisplay.length / ordersPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Tabs for orders and cancelled orders

    return (
        <>
            <div className="min-h-screen bg-gray-100 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}

                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">
                            {domainType === "RETAIL" ? "My Orders" : "My Enrollments"}
                        </h1>
                        <div className="mt-4 lg:mt-0">
                            <input
                                type="text"
                                placeholder="Search orders..."
                                className="w-full lg:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Tabs and Sorting */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                        <div className="flex space-x-4 mb-4 sm:mb-0">
                            <button
                                onClick={() => setActiveTab("orders")}
                                className={`px-4 py-2 rounded-lg font-medium ${activeTab === "orders" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
                            >
                                Orders
                            </button>
                            <button
                                onClick={() => setActiveTab("cancelled")}
                                className={`px-4 py-2 rounded-lg font-medium ${activeTab === "cancelled" ? "bg-blue-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
                            >
                                Cancelled Orders
                            </button>
                        </div>
                        <div className="flex space-x-4">
                            <button
                                onClick={toggleSortByPrice}
                                className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100"
                            >
                                Sort by Price {sortByPriceAsc ? "↑" : "↓"}
                            </button>
                            <button
                                onClick={toggleSortByRecent}
                                className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100"
                            >
                                Sort by Recent {sortByRecentAsc ? "↑" : "↓"}
                            </button>
                        </div>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <Loading />
                        </div>
                    ) : (
                        <>
                            {/* Orders List */}
                            {ordersToDisplay.length > 0 ? (
                                <div className="space-y-4">
                                    {currentOrders.map((order) => (
                                        <div key={order._id}>
                                            {isBulkEnabledOrder(order) && (() => {
                                                const progress = getBulkProgress(order);
                                                const status = order.currentStatus === 'PAYMENT_PROCESSING'
                                                    ? 'Waiting for admin approval'
                                                    : order.currentStatus === 'PAYMENT_PENDING'
                                                        ? 'Payment pending — invoice available'
                                                        : String(order.currentStatus || '').replace(/_/g, ' ');
                                                return (
                                                    <div className="mb-1 rounded-t-lg bg-amber-50 px-4 py-2 text-sm text-amber-800">
                                                        <div className="font-semibold">Bulk booking · {order.bulkOrderQuantity || order.lineItemList?.[0]?.quantity || 0} units · {status}</div>
                                                        {progress && <div className="mt-1 text-xs">Group progress: {progress.bookedQuantity}{progress.minimumQuantity ? ` / ${progress.minimumQuantity} units` : ' units'}{progress.minimumAmount ? ` · ${progress.bookedAmount.toFixed(2)} / ${progress.minimumAmount.toFixed(2)} amount` : ''}</div>}
                                                        {order.currentStatus === 'PAYMENT_PENDING' && getRefundAwareOrderStatus(order) !== 'CANCELLED' && (
                                                            <button type="button" onClick={() => handleBulkPayNow(order)} disabled={payingOrderId === order.primaryId || order.currentStatus === 'PAID'} className="mt-2 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                                                                {payingOrderId === order.primaryId ? 'Opening payment…' : 'Pay Now'}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <OrderLayout orderDetail={order} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">No orders found.</p>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-8 flex justify-center">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {bulkPaymentError && <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{bulkPaymentError}</div>}

                    {/* Server Error */}
                    {isServerError && (
                        <div className="text-center py-8">
                            <p className="text-red-500">Unable to connect to the server. Please try again later.</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ViewOrders;
